/**
 * Global fleet-health indicator (topbar).
 *
 * Born from a real incident: a pool sat in "Launching" for an hour
 * while capacity rejections fired every 15 minutes, and the only way
 * to learn the fleet was unhealthy was to open the right pool's
 * Events tab. This strip makes the two degraded triage buckets
 * (`Pool.attention_level`, derived server-side) visible from EVERY
 * page:
 *
 *   - red   "N need attention" — a human must act (needs_attention).
 *   - amber "N self-healing"   — degraded but auto-recovering
 *     (capacity wait, retry backoff). Watching is enough.
 *
 * Clicking a chip navigates to the (first) affected pool's dashboard;
 * the tooltip lists every affected pool with its honest status chip.
 * Healthy fleets render nothing — the indicator only exists when
 * there is something to know.
 *
 * Data comes from the same TanStack queries the Workloads tree uses
 * (`useServices` / `useAllPools`), so the cache is shared and the
 * topbar's global auto-refresh keeps the counts live on every page.
 */
import { useNavigate } from "react-router-dom";

import { resolvePoolLabel } from "@/api/local/labels";
import type { Pool } from "@/api/types";
import { Pill } from "@/components/Pill";
import { useAllPools, useServices } from "@/workloads/data";

export function FleetAttention() {
  const services = useServices();
  const pools = useAllPools(services.data?.map((s) => s.id));

  const actionRequired = (pools.data ?? []).filter(
    (p) => p.attention_level === "action_required",
  );
  const selfHealing = (pools.data ?? []).filter(
    (p) => p.attention_level === "self_healing",
  );

  if (actionRequired.length === 0 && selfHealing.length === 0) return null;

  return (
    <div className="fleet-attention" role="status" aria-live="polite">
      {actionRequired.length > 0 && (
        <AttentionChip
          pools={actionRequired}
          tone="danger"
          pulse
          label={`${actionRequired.length} need attention`}
        />
      )}
      {selfHealing.length > 0 && (
        <AttentionChip
          pools={selfHealing}
          tone="warn"
          label={`${selfHealing.length} self-healing`}
        />
      )}
    </div>
  );
}

function AttentionChip({
  pools,
  tone,
  label,
  pulse,
}: {
  pools: Pool[];
  tone: "danger" | "warn";
  label: string;
  pulse?: boolean;
}) {
  const navigate = useNavigate();
  const title = pools
    .map((p) => `${resolvePoolLabel(p)}: ${p.status_summary || p.status_chip}`)
    .join("\n");
  return (
    <button
      type="button"
      className="fleet-attention__chip"
      title={title}
      aria-label={`${label} — ${title}`}
      onClick={() =>
        // Jump straight to the affected pool. With several affected,
        // the first is still the right landing spot: its dashboard
        // banner + the tree's amber/red chips take over from there.
        navigate(`/?focus=pool:${pools[0].id}&tab=dashboard`)
      }
    >
      <Pill tone={tone} pulse={pulse}>
        {label}
      </Pill>
    </button>
  );
}
